import postalCodeRegexMap from './postal-code-regex.json'
import {PhoneNumberType, PhoneNumberUtil} from 'google-libphonenumber'
import countryRegionJSON from './country-region.json'
import {isSelectableCountry} from './selectable-countries'

// Initialize instance
const phoneUtil = PhoneNumberUtil.getInstance()

// Pre-compile postal-code regexes once at module load.
// The patterns are static, bundled, and developer-controlled — no untrusted
// regex source ever reaches new RegExp(). The lookup key (countryCode) is the
// only user-controlled value and it only selects from this fixed allowlist.
const compiledPostalCodeRegex = Object.freeze(
  Object.fromEntries(
    Object.entries(postalCodeRegexMap).map(([code, {regex}]) => [
      code,
      new RegExp(regex, 'i'), // nosemgrep: eslint.detect-non-literal-regexp -- bundled JSON allowlist, not user input
    ]),
  ),
)

// Cap input length defensively — postal codes are at most ~12 chars worldwide.
// Bounds worst-case regex.test() runtime regardless of pattern quality.
const MAX_POSTAL_CODE_LENGTH = 32

export const validatePostalCode = (countryCode, postalCode) => {
  if (!countryCode || !postalCode) return false
  const trimmed = postalCode.trim()
  if (trimmed.length > MAX_POSTAL_CODE_LENGTH) return false
  const regex = compiledPostalCodeRegex[countryCode.toUpperCase()]
  if (!regex) {
    // Country code not found in the map
    console.warn(
      `No postal code pattern found for country code: ${countryCode}`,
    )
    return false
  }
  return regex.test(trimmed)
}

export const validatePhoneNumber = (countryCode, phoneNumber) => {
  if (!countryCode || !phoneNumber) return false

  try {
    const parsedNumber = phoneUtil.parse(phoneNumber, countryCode.toUpperCase())
    return phoneUtil.isValidNumberForRegion(
      parsedNumber,
      countryCode.toUpperCase(),
    )
  } catch (error) {
    // Parsing failed (invalid number format etc.)
    return false
  }
}

/**
 * Country-aware mobile check, layered on the same google-libphonenumber
 * metadata validatePhoneNumber uses. For sending an SMS the number has to be
 * a handset, not a landline.
 *
 * Known limitation: the US and CA numbering plans do not separate mobile from
 * landline, so libphonenumber reports FIXED_LINE_OR_MOBILE for every US/CA
 * number and those are accepted. Countries that do separate them (AU, NZ, GB
 * and most others) get a real answer, and a definite landline is rejected.
 * Telling a US mobile from a US landline needs a carrier lookup service.
 *
 * @param {string} countryCode  ISO-2 country code
 * @param {string} phoneNumber  number as typed
 * @returns {boolean} false when the number is invalid or is known to be a landline
 */
export const validateMobileNumber = (countryCode, phoneNumber) => {
  if (!validatePhoneNumber(countryCode, phoneNumber)) return false

  try {
    const parsedNumber = phoneUtil.parse(phoneNumber, countryCode.toUpperCase())
    const numberType = phoneUtil.getNumberType(parsedNumber)
    return (
      numberType === PhoneNumberType.MOBILE ||
      numberType === PhoneNumberType.FIXED_LINE_OR_MOBILE
    )
  } catch (error) {
    return false
  }
}

export const fetchSelectedCountryRegionList = countryCode => {
  if (!isSelectableCountry(countryCode)) return []

  let selectedCountryJSON = countryRegionJSON.find(
    item => item.code === countryCode,
  )

  return (selectedCountryJSON?.regions || [])
    .map(({name, code}) => ({
      name,
      value: code,
    }))
    .sort((a, b) => (a.name > b.name ? 1 : -1))
}
